import React, { ReactNode } from "react";
import { View } from "@src/components";
import { useIsOnline } from "@src/utils/network";
import { FullScreenLoading } from "./FullScreenLoading";
import { FullScreenError } from "./FullScreenError";
import { EmptyState, type EmptyStateProps } from "./EmptyState";
import { StaleDataBanner } from "./StaleDataBanner";
import { ForbiddenError } from "@src/features/auth";
import { ForbiddenContent } from "@src/features/auth/components/ForbiddenContent";

export interface DataStateViewProps<T> {
  /** The data from React Query */
  data: T | undefined;
  /** True when loading with no cache. Use `isLoading` from React Query. */
  isLoading: boolean;
  /** True when any fetch is in progress. Use `isFetching` from React Query. */
  isFetching: boolean;
  /** Error from the query, if any */
  error: Error | null;
  /** Refetch function from React Query */
  refetch: () => void;
  /** Check if data is "empty" (e.g., empty array). If not provided, only undefined/null is considered empty. */
  isEmpty?: (data: T) => boolean;
  /** Render function that receives the data. Enforces type-safe data access. */
  children: (data: T) => ReactNode;
  /** Loading screen message */
  loadingMessage?: string;
  /** Error screen title (only shown when no cache available) */
  errorTitle?: string;
  /** Error screen message (only shown when no cache available) */
  errorMessage?: string;
  /** Empty state config - required if isEmpty is provided */
  emptyState?: EmptyStateProps;
  /** Whether to show the stale data banner when showing cached data with error. Default: true */
  showStaleDataBanner?: boolean;
}

/**
 * Orchestrator component for handling screen display states.
 *
 * Handles 4 states:
 * 1. Loading (no cache) - Shows full screen loading spinner
 * 2. Error (no cache) - Shows full screen error with retry
 * 3. Empty data - Shows empty state view
 * 4. Has data (possibly stale) - Shows content with optional stale banner
 *
 * @example
 * ```tsx
 * <DataStateView
 *   data={cameras}
 *   isLoading={isLoading}
 *   isFetching={isFetching}
 *   error={error}
 *   refetch={refetch}
 *   isEmpty={(cameras) => cameras.length === 0}
 *   emptyState={{
 *     icon: "videocam-off-outline",
 *     title: "No cameras found",
 *     message: "There are no cameras available.",
 *   }}
 * >
 *   {(cameras) => <CameraList cameras={cameras} />}
 * </DataStateView>
 * ```
 */
export function DataStateView<T>({
  data,
  isLoading,
  isFetching,
  error,
  refetch,
  isEmpty,
  children,
  loadingMessage,
  errorTitle = "Unable to load",
  errorMessage,
  emptyState,
  showStaleDataBanner = true,
}: DataStateViewProps<T>) {
  const isOnline = useIsOnline();

  // Derive named state flags for readability
  const hasData = data !== undefined && data !== null;
  const isDataEmpty = hasData && isEmpty ? isEmpty(data) : false;
  const hasAnyData = hasData && !isDataEmpty;
  const hasError = error !== null;

  // If we have data, show it even if isLoading is true (hydration case)
  // Only show loading if we truly have no data
  const isInitialLoading = isLoading && !hasData;
  // During hydration, if we're fetching and have no data yet, wait a bit before showing error
  // This handles the case where hydration is in progress and refetch fails immediately
  const isErrorWithoutData = hasError && !hasData && !isFetching;
  const isErrorWithCachedData = hasError && hasAnyData;
  const isEmptyState = hasData && isDataEmpty;
  const isFetchingEmpty = isFetching && isEmptyState && !hasError;

  // STATE 1: Initial loading (no cached data yet)
  // Skip loading if we have data (hydration case - data is available from cache)
  if (isInitialLoading || (isFetching && !hasData && !hasError)) {
    return <FullScreenLoading message={loadingMessage} />;
  }

  // Keep spinner while empty data is being refreshed.
  if (isFetchingEmpty) {
    return <FullScreenLoading message={loadingMessage} />;
  }

  // STATE 2: No cached data and error occurred (only if not currently fetching)
  if (isErrorWithoutData) {
    // AuthZ: 403 Forbidden – show a dedicated inline forbidden state instead of
    // a generic error, so the host screen shell (e.g. tabs) stays visible.
    if (error instanceof ForbiddenError) {
      return (
        <View flex={1} bg="background">
          <ForbiddenContent showActions={false} />
        </View>
      );
    }

    return (
      <FullScreenError
        title={errorTitle}
        message={errorMessage || error?.message || "Please check your connection and try again."}
        onRetry={isOnline ? refetch : undefined}
        isRetrying={isFetching}
        isOffline={!isOnline}
      />
    );
  }

  // STATE 3: Data loaded but is empty
  if (isEmptyState && emptyState) {
    return <EmptyState {...emptyState} />;
  }

  // STATE 4 & 5: Has cached data (show it, with optional stale indicator)
  if (hasAnyData) {
    return (
      <View flex={1}>
        {showStaleDataBanner && isErrorWithCachedData && (
          <StaleDataBanner isOffline={!isOnline} isRetrying={isFetching} onRetry={isOnline ? refetch : undefined} />
        )}
        {children(data)}
      </View>
    );
  }

  // Fallback: still loading (shouldn't normally reach here)
  return <FullScreenLoading message={loadingMessage} />;
}
